import {User} from '@hconnect/apiclient'
import {trackEvent} from '@hconnect/common/logging/Analytics'
import {useBreakPoints} from '@hconnect/uikit'
import {Button, DropdownListItem, DropdownWithButton} from '@hm/fundamental'
import {
  mdiKeyOutline,
  mdiTrashCanOutline,
  mdiDotsVertical,
  mdiEmailOutline,
  mdiAccountOutline,
  mdiUpdate,
  mdiLockOutline
} from '@mdi/js'
import {AxiosError} from 'axios'
import {useSnackbar} from 'notistack'
import React, {useState} from 'react'

import {useUserRoles} from '../../common/hooks/useUserRoles'
import {ProductActivityDialog} from '../../components/UserInformation/dialogs/ProductActivityDialog/ProductActivityDialog'
import {UserMessagesDialog} from '../../components/UserInformation/dialogs/ProductActivityDialog/UserMessagesDialog'
import {useLoggedInUserPermissions} from '../../hooks/useLoggedInUser'
import {useTranslation} from '../../hooks/useTranslation'
import {Permission} from '../../modules/LoggedInUser'

import {CloneUserButton} from './CloneUserButton'
import {AuditInformationDialog} from './dialogs/AuditInformationDialog'
import {CloneUserDialog} from './dialogs/CloneUserDialog'
import {DeleteUserDialog} from './dialogs/DeleteUserDialog'
import {ResetPasswordDialog} from './dialogs/ResetPasswordDialog'
import {useCloneUser} from './hooks/useCloneUser'
import {useDeleteUser} from './hooks/useDeleteUser'
import {useResetPassword} from './hooks/useResetPassword'

type ManageUserActionsProps = {
  loggedInUserPermissions?: Permission[] | null
  user?: User
  loggedInUserProfile?: User | null
  isPilot?: boolean
}

// eslint-disable-next-line complexity
export const ManageUserActions = ({
  loggedInUserPermissions,
  user,
  loggedInUserProfile,
  isPilot
}: ManageUserActionsProps) => {
  const {t} = useTranslation()
  const {enqueueSnackbar} = useSnackbar()
  const screenSizes = useBreakPoints()
  const isTablet = ['xs', 'sm', 'md'].includes(screenSizes)
  const loggedInUserPermissionsData = useLoggedInUserPermissions(loggedInUserPermissions)
  const [showCloneDialog, setShowCloneDialog] = useState(false)
  const [showDeleteUserDialog, setShowDeleteUserDialog] = useState(false)
  const [showResetPasswordDialog, setShowResetPasswordDialog] = useState(false)
  const [showUsersActivityDialog, setShowUsersActivityDialog] = useState(false)
  const [showUsersMessagesDialog, setShowUsersMessagesDialog] = useState(false)

  const {data, refetch, isFetching} = useCloneUser(user?.user_id || '')
  const [auditType, setAuditType] = useState<string>('')
  const canResetPassword = loggedInUserPermissionsData.some(
    (permission) => permission.permissionType === 'CHANGE_USER_LOGIN'
  )

  const canDeleteUser =
    loggedInUserPermissionsData.some(
      (permission) => permission.permissionType === 'DELETE_USERS'
    ) || loggedInUserProfile?.user_id === user?.user_id

  const canSeeMessageHistory = loggedInUserPermissionsData.some(
    (permission) => permission.permissionType === 'MANAGE_MESSAGES'
  )

  const {handleDeleteUser} = useDeleteUser({setShowDeleteUserDialog, userData: user})
  const {handleResetPassword} = useResetPassword({
    setShowResetPasswordDialog,
    userData: user,
    loggedInUserProfile: loggedInUserProfile
  })

  const {data: roles} = useUserRoles(user?.user_id)

  const handleCloneUser = async () => {
    const {data, isError, error} = await refetch()
    if (isError) {
      const err = error as AxiosError
      console.error(err)
      trackEvent('adminConsoleError', {
        product: 'adminConsole',
        date: new Date().toISOString(),
        errorCode: err?.response?.status,
        component: 'ManageUser.tsx Reset password',
        endpoint: err?.response?.config?.url
      })
      enqueueSnackbar(
        err?.response?.status === 400 || err?.response?.status === 403
          ? t('manageUser.hasNoPermissions')
          : t('manageUser.cloneError'),
        {variant: 'error'}
      )
      return
    }
    if (data && data.length > 0) {
      setShowCloneDialog(true)
    } else {
      enqueueSnackbar(t('manageUser.cloneError'), {variant: 'error'})
    }
  }

  return (
    <>
      <div className="flex gap-3">
        {!isPilot && !isTablet && (
          <div className="flex gap-3">
            <CloneUserButton
              isLoading={isFetching}
              onClick={handleCloneUser}
              loggedInUserProfile={loggedInUserProfile}
              newUserProfile={true}
            />
            {canResetPassword && (
              <Button
                icon={mdiKeyOutline}
                onClick={() => setShowResetPasswordDialog(true)}
                data-test-id="button-reset-password"
              >
                {t('manageUser.resetPassword')}
              </Button>
            )}
            {canDeleteUser && (
              <Button
                icon={mdiTrashCanOutline}
                onClick={() => setShowDeleteUserDialog(true)}
                data-test-id="button-delete-user"
              >
                {t('manageUser.deleteUser')}
              </Button>
            )}
          </div>
        )}
        {!isPilot && (
          <DropdownWithButton
            className="z-10"
            buttonProps={{
              square: true,
              variant: 'primary',
              size: 'regular',
              icon: mdiDotsVertical
            }}
            data-cy="manage-user-actions-more"
          >
            {isTablet && (
              <DropdownListItem
                icon={mdiLockOutline}
                iconClassName="text-foreground"
                title={t('manageUser.cloneUser')}
                onClick={handleCloneUser}
                data-cy="button-clone-user"
              />
            )}
            {isTablet && canResetPassword && (
              <DropdownListItem
                icon={mdiKeyOutline}
                iconClassName="text-foreground"
                title={t('manageUser.resetPassword')}
                onClick={() => setShowResetPasswordDialog(true)}
                data-cy="button-reset-password"
              />
            )}
            {isTablet && canDeleteUser && (
              <DropdownListItem
                icon={mdiTrashCanOutline}
                iconClassName="text-foreground"
                title={t('manageUser.deleteUser')}
                onClick={() => setShowDeleteUserDialog(true)}
                data-cy="button-delete-user"
              />
            )}
            <DropdownListItem
              icon={mdiUpdate}
              iconClassName="text-foreground"
              title={t('audit.viewUserAudit')}
              onClick={() => setAuditType('user')}
              data-cy="button-user-audit-information"
            />
            <DropdownListItem
              icon={mdiUpdate}
              iconClassName="text-foreground"
              title={t('audit.viewRoleAudit')}
              onClick={() => setAuditType('role')}
              data-cy="button-role-audit-information"
            />
            <DropdownListItem
              icon={mdiAccountOutline}
              iconClassName="text-foreground"
              title={t('manageUser.userActivity')}
              onClick={() => setShowUsersActivityDialog(true)}
              data-cy="button-user-activity"
            />
            {canSeeMessageHistory && (
              <DropdownListItem
                icon={mdiEmailOutline}
                iconClassName="text-foreground"
                title={t('manageUser.userMessages')}
                onClick={() => setShowUsersMessagesDialog(true)}
                data-cy="button-user-messages"
              />
            )}
          </DropdownWithButton>
        )}
      </div>
      {showCloneDialog && data && (
        <CloneUserDialog
          showCloneUserDialog={showCloneDialog}
          setShowCloneUserDialog={setShowCloneDialog}
          roles={roles?.sort((a, b) => data.indexOf(b.id) - data.indexOf(a.id)) || []}
          clonableRoleIds={data}
          userId={user?.user_id || ''}
        />
      )}
      {user && showDeleteUserDialog && (
        <DeleteUserDialog
          showDeleteUserDialog={showDeleteUserDialog}
          setShowDeleteUserDialog={setShowDeleteUserDialog}
          handleDeleteUser={handleDeleteUser}
        />
      )}
      {user && (
        <ResetPasswordDialog
          showResetPasswordDialog={showResetPasswordDialog}
          setShowResetPasswordDialog={setShowResetPasswordDialog}
          handleResetPassword={handleResetPassword}
        />
      )}
      {auditType && (
        <AuditInformationDialog
          userIdentifier={user?.id ?? ''}
          auditType={auditType}
          onCloseAuditDialog={() => setAuditType('')}
        />
      )}
      {showUsersActivityDialog && (
        <ProductActivityDialog
          open={showUsersActivityDialog}
          setOpenModal={setShowUsersActivityDialog}
          isLoading={false}
          lastActivityDates={user?.lastActivityDates}
        />
      )}
      {showUsersMessagesDialog && (
        <UserMessagesDialog
          open={showUsersMessagesDialog}
          setOpenModal={setShowUsersMessagesDialog}
          userId={user?.user_id || ''}
        />
      )}
    </>
  )
}
